<template>
  <div class="relative flex-shrink-0 w-90">
    <div
      class="h-12.5 px-3 flex items-center justify-between gap-4 flex-shrink-0"
    >
      <div class="flex items-center gap-2 truncate" :title="stage.name">
        <BlTextSubtitle1 :text="stage.name" class="truncate" />
        <BlBubbleCount
          v-if="!editState"
          :count="stage.number_of_prospect || 0"
        />
      </div>
      <div>
        <BlInputButton
          v-if="!editState"
          type="plain"
          dense
          :label="showDetails ? 'Hide details' : 'View details'"
          @clicked="showDetails = !showDetails"
        />
        <div
          v-if="
            editable &&
              !stage.is_admitted &&
              !stage.is_pending &&
              !stage.is_discontinued &&
              stage.position !== 1
          "
          class="handle_move_position h-6.5 w-30 flex items-center justify-center rounded-full bg-pure-white border border-slate-500 cursor-grab"
        >
          <BlIcon
            icon="drag"
            color="slate-6"
            class="w-5 h-5"
            :style="{ rotate: '90deg' }"
          />
        </div>
      </div>
    </div>
    <div
      class="relative flex flex-col overflow-y-auto border rounded shadow-md bg-pure-white border-slate-400"
      :style="{ height: 'calc(100vh - 10rem - 3.625rem)' }"
    >
      <div v-if="editState" class="flex flex-col justify-between h-full">
        <div class="p-3">
          <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between gap-2">
              <BlTextSubtitle3 text="Stage name" class="text-slate-800" />
              <div
                v-if="editState"
                class="flex items-center flex-shrink-0 h-6 gap-2 px-2 rounded-full bg-success-100"
              >
                <BlIcon
                  icon="update"
                  color="success"
                  class="w-4 h-4"
                  :class="{ 'animate-spin': saveLoading }"
                />
                <BlTextBody2
                  v-if="saveLoading"
                  class="text-success"
                  text="Saving"
                />
                <BlTextBody2 v-else class="text-success" text="Saved" />
              </div>
            </div>
            <BlInputText :input="nameInput" @changed="onInput" />
          </div>
          <div class="my-4 h-0.5 w-full bg-slate-300 rounded"></div>
          <div class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
              <BlTextSubtitle3
                text="Stage description"
                class="text-slate-800"
              />
              <BlTextBody2 text="Create a brief description of this stage." />
            </div>
            <BlInputText :input="descriptionInput" @changed="onInput" />
          </div>
          <div class="my-4 h-0.5 w-full bg-slate-300 rounded"></div>
          <div class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
              <BlTextSubtitle3
                text="Stage instructions"
                class="text-slate-800"
              />
              <BlTextBody2
                text="Stage instructions are predefined instructions that automatically appear when a prospect moves to this stage."
              />
            </div>
            <div class="flex flex-col gap-2">
              <PipelineInstructionStage
                v-for="doc in stage.documents"
                :key="doc.id"
                :instruction="doc"
                :stage="stage"
                editable
              />
            </div>
            <div>
              <BlInputButton
                type="plain"
                dense
                icon="plus"
                label="Add instruction"
                @clicked="newInstructionOpened = true"
              />
            </div>
          </div>
        </div>
        <div
          class="sticky bottom-0 flex items-center justify-center flex-shrink-0 h-10 gap-16 bg-pure-white"
        >
          <BlInputButton
            v-if="
              !stage.is_admitted && !stage.is_pending && !stage.is_discontinued &&
                stage.position !== 1
            "
            type="plain-error"
            label="Delete stage"
            @clicked="deleteOpened = true"
          />
          <BlInputButton
            v-if="!editable"
            type="plain"
            label="Close"
            @clicked="editState = false"
          />
        </div>
      </div>
      <div v-else-if="showDetails" class="flex flex-col justify-between h-full">
        <div class="p-3">
          <div class="mb-4">
            <BlTextBody2
              v-if="stage.description"
              :text="stage.description || '-'"
              class="whitespace-pre-line text-slate-800"
            />
            <BlTextBody2
              v-else
              text="No description"
              class="italic text-slate-800"
            />
          </div>
          <div class="flex items-center gap-2 mb-2">
            <div
              class="h-7.5 w-7.5 flex-shrink-0 flex items-center justify-center bg-slate-200 rounded"
            >
              <BlIcon icon="to-do" color="slate-6" class="w-5 h-5" />
            </div>
            <BlTextSubtitle3 text="Stage instructions" />
          </div>
          <div v-if="!stage.documents.length">
            <BlTextBody2 text="No instruction" class="italic text-slate-800" />
          </div>
          <div v-else class="flex flex-col gap-2">
            <PipelineInstructionStage
              v-for="doc in stage.documents"
              :key="doc.id"
              :instruction="doc"
              :stage="stage"
            />
          </div>
        </div>
        <div
          class="sticky bottom-0 flex items-center justify-center flex-shrink-0 h-10 bg-pure-white"
        >
          <BlInputButton
            type="plain"
            label="Edit stage"
            @clicked="editState = true"
          />
        </div>
      </div>
      <div v-else class="flex flex-col p-3">
        <div v-if="fetchLoading" class="grid w-full h-92 place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!stage.prospect_stages.length"
          class="grid w-full h-92 place-items-center"
        >
          <BlTextBody2
            text="Oops, there are no prospect available."
            class="text-center text-slate-700"
          />
        </div>
        <div v-else class="flex flex-col gap-3">
          <PipelineProspectStage
            v-for="prospectStage in stage.prospect_stages"
            :key="prospectStage.id"
            :item="prospectStage"
            :dragId="dragId"
            @drag-start="$emit('drag-start', $event)"
          />
        </div>
        <BlInputPagination
          v-if="stage.number_of_prospect > perPage"
          class="sticky z-10 pt-3 bottom-3"
          :count="Math.ceil(stage.number_of_prospect / perPage)"
          :page="page"
          @changed="changePage"
        />
      </div>
    </div>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete stage"
      label="Confirm"
      type="error"
      :disabled="disabledDelete"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    >
      <div v-if="!stage.number_of_prospect" class="px-8">
        <BlTextBody2 text="You are about to remove the " />
        <BlTextBody2 :text="stage.name" class="font-bold" />
        <BlTextBody2 text="stage. Are you sure you want to proceed?" />
      </div>
      <div v-else class="px-8">
        <div class="mb-4">
          <BlTextBody2
            :text="`You have ${stage.number_of_prospect} prospect(s) in the `"
          />
          <BlTextBody2 :text="stage.name" class="font-bold" />
          <BlTextBody2
            text="stage. Please select where these prospects will be moved to:"
          />
        </div>
        <div class="mb-4">
          <BlSelectBox
            v-model="movedStageInput"
            placeholder="Select stage"
            :options="stageOptions"
            search
            @change="movedStageInput = $event"
          />
        </div>
        <div v-if="destinationStage.is_discontinued" class="mb-4">
          <BlTextBody2
            text="Please provide the reason for the prospect's decision to discontinue during the client acquisition process. If the prospect returns, their profile can be reopened at any time."
          />
          <BlSelectBox
            class="mt-4"
            v-model="reasonInput"
            placeholder="Select reason"
            :options="reasonOptions"
            @change="reasonInput = $event"
          />
          <BlInputText
            v-if="reasonInput === 'other'"
            class="mt-2"
            :input="otherReasonInput"
            @changed="inputHandler"
          />
        </div>
        <div class="mb-4">
          <BlTextBody2
            text="Alternatively, you can manually move these prospects to another stage before deleting this one."
          />
        </div>
      </div>
      <div
        v-if="deleteLoading"
        class="absolute flex justify-end w-full px-16 -bottom-12"
      >
        <div class="blum-loader-btn"></div>
      </div>
    </BlBackdrop>

    <NewInstructionStagePanel
      :open="newInstructionOpened"
      :stage="stage"
      @close-panel="newInstructionOpened = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import debounce from "lodash/debounce";

import PipelineInstructionStage from "./InstructionStage.vue";
import PipelineProspectStage from "./ProspectStage.vue";
import NewInstructionStagePanel from "../modal/NewInstructionStagePanel.vue";

import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlBubbleCount from "../../BlBubbleCount.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputText from "../../BlInputText.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlInputPagination from "../../BlInputPagination.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "PipelineStage",
  components: {
    PipelineInstructionStage,
    PipelineProspectStage,
    NewInstructionStagePanel,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextBody2,
    BlBubbleCount,
    BlIcon,
    BlInputButton,
    BlInputText,
    BlSelectBox,
    BlInputPagination,
    BlBackdrop
  },
  props: {
    stage: { type: Object, required: true },
    editable: { type: Boolean, default: false },
    dragId: { type: Number, default: 0 }
    // @drag-start
  },
  data() {
    return {
      editState: false,
      showDetails: false,
      fetchLoading: false,
      page: 1,
      perPage: 25,
      //
      saveLoading: false,
      nameInput: {
        placeholder: "Type stage name",
        model: "",
        disabled: false
      },
      descriptionInput: {
        textarea: true,
        placeholder: "Type stage description",
        model: ""
      },
      //
      deleteOpened: false,
      deleteLoading: false,
      movedStageInput: 0,
      reasonInput: "",
      reasonOptions: [
        { value: "fees_too_high", label: "Fees too high" },
        { value: "distance_too_far", label: "Distance too far" },
        {
          value: "unable_to_contact_family",
          label: "Unable to contact family"
        },
        { value: "hours_dont_fit_schedule", label: "Hours don't fit schedule" },
        {
          value: "seeking_service_not_provided",
          label: "Seeking services not provided"
        },
        { value: "too_old", label: "Too old" },
        {
          value: "resides_in_a_different_state",
          label: "Resides in a different state"
        },
        { value: "no_show_response", label: "No show/response" },
        { value: "do_not_need_therapy", label: "Do not need therapy" },
        { value: "ongoing_client", label: "Ongoing client" },
        { value: "other", label: "Other" }
      ],
      otherReasonInput: {
        textarea: true,
        placeholder: "Type the reason",
        model: ""
      },
      //
      newInstructionOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "stages"]),
    destinationStage() {
      if (!this.movedStageInput) return {};
      return this.stages.find(i => i.id === this.movedStageInput) || {};
    },
    stageOptions() {
      return this.stages
        .filter(i => i.id !== this.stage.id && !i.is_admitted)
        .map(i => ({ value: i.id, label: i.name }));
    },
    disabledDelete() {
      if (this.deleteLoading) return true;
      if (this.stage.number_of_prospect) {
        if (!this.movedStageInput) return true;
        if (this.destinationStage.is_discontinued) {
          if (!this.reasonInput) return true;
          if (this.reasonInput === "other") {
            if (!this.otherReasonInput.model) return true;
          }
        }
      }
      return false;
    }
  },
  watch: {
    editable(val) {
      this.editState = val;
    },
    editState(val) {
      if (val) {
        this.resetInput();
      }
    },
    showDetails(val) {
      if (!val) {
        this.fetchStageProspects();
      }
    }
  },
  mounted() {
    this.editState = this.editable;
    this.fetchStageProspects();
  },
  methods: {
    ...mapActions([
      "getStageProspectStages",
      "updateStage",
      "moveProspectStages",
      "deleteStage"
    ]),
    fetchStageProspects() {
      const data = {
        id: this.stage.id,
        params: `?page=${this.page}&per_page=${this.perPage}`
      };
      this.fetchLoading = true;
      return this.getStageProspectStages(data).then(({ success, data }) => {
        this.fetchLoading = false;
        // if (!success) return;
      });
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchStageProspects();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchStageProspects();
    },
    resetInput() {
      this.nameInput.model = this.stage.name;
      if (
        this.stage.is_admitted ||
        this.stage.is_pending ||
        this.stage.is_discontinued
      ) {
        this.nameInput.disabled = true;
      }
      this.descriptionInput.model = this.stage.description;
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    onInput(payload) {
      payload.input.model = payload.value;
      this.onEditing();
    },
    onEditing: debounce(function() {
      const data = {
        id: this.stage.id,
        stage: {
          name: this.nameInput.model,
          description: this.descriptionInput.model
        }
      };
      if (
        this.stage.name === data.stage.name &&
        this.stage.description === data.stage.description
      ) {
        return;
      }
      this.saveLoading = true;
      this.updateStage(data).then(({ success }) => {
        this.saveLoading = false;
        if (!success) {
          return this.resetInput();
        }
      });
    }, 1000),
    onRemove() {
      if (this.stage.number_of_prospect) {
        this.moveProcess();
      } else {
        this.deletePorcess();
      }
    },
    moveProcess() {
      const data = {
        id: this.stage.id,
        stage_id: this.movedStageInput
      };
      if (this.destinationStage.is_discontinued) {
        data["discontinuation_reason"] = this.reasonInput;
        if (this.reasonInput === "other") {
          data["other_discontinuation_reason"] = this.otherReasonInput.model;
        }
      }
      this.deleteLoading = true;
      this.moveProspectStages(data).then(({ success }) => {
        if (!success) {
          this.deleteLoading = false;
          return;
        }
        this.getStageProspectStages({ id: this.movedStageInput });
        this.deletePorcess();
      });
    },
    deletePorcess() {
      const data = {
        id: this.stage.id,
        stage_name: this.stage.name
      };
      this.deleteLoading = true;
      this.deleteStage(data).then(({ success }) => {
        this.deleteLoading = false;
        if (!success) return;
        this.deleteOpened = false;
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/pipeline/Stage.vue